/**
 * O formulário de parametrização de um ambiente do conector gov.br, mais o
 * botão "Testar conexão".
 *
 * Componente PURO DE APRESENTAÇÃO: não chama a API. Quem chama é `dados.ts`
 * (`useGravarAmbiente`, `useTestarConexao`), e quem liga os dois é a página —
 * este arquivo só recebe dados por prop e devolve intenção por callback.
 *
 * O QUE NÃO EXISTE AQUI, DE PROPÓSITO: campo para o segredo do cliente. A
 * tabela `govbr_ambientes` não tem coluna para ele e a resposta da API não o
 * carrega — ele vive no cofre do ambiente e quem o usa é o Keycloak
 * (docs/CONECTOR-GOVBR.md, seção 5; contrato.ts, comentário de topo). Um
 * campo aqui — mascarado ou não — seria convite para alguém "completar" o
 * formulário depois, achando que faltou alguma coisa. Não falta: o lugar
 * certo do segredo não é esta tela.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle2, Info, Loader2 } from 'lucide-react';
import { type ReactElement, useEffect, useId, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { cn } from '@/nucleo/utilitarios';
import { Alert, AlertDescription, AlertTitle } from '@/ui/base/alert';
import { Button } from '@/ui/base/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/ui/base/form';
import { Input } from '@/ui/base/input';
import { RadioGroup, RadioGroupItem } from '@/ui/base/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Switch } from '@/ui/base/switch';
import { StatusBadge } from '@/ui/StatusBadge';

import {
  type AmbienteGovbr,
  type DadosAmbiente,
  type Modo,
  type ResultadoTeste,
  DESCRICAO_DO_MODO,
  MODOS,
  NIVEIS_MINIMOS,
  ROTULO_DO_MODO,
  ROTULO_DO_NIVEL,
  dataHoraLegivel,
  duracaoLegivel,
  escoposDoTexto,
  textoDosEscopos,
} from './contrato';

export interface FormularioDoAmbienteProps {
  /** A linha do ambiente, como o GET devolveu. É o valor inicial dos campos. */
  ambiente: AmbienteGovbr;
  /** Envia o conjunto inteiro. PUT substitui a representação: nada de parcial. */
  aoGravar: (dados: DadosAmbiente) => void;
  gravando: boolean;
  aoTestar: () => void;
  testando: boolean;
  /** Resultado do último teste disparado nesta sessão, se houve. */
  resultado?: ResultadoTeste | null;
}

/**
 * Texto de uma palavra só, sem espaço e sem vazio.
 *
 * Espelha `SEM_ESPACO` de `apps/api/src/modulos/govbr/govbr.dto.ts` — não é
 * importável de lá (front não depende da API), mas a regra é a mesma: escopo
 * e apelido do provedor não podem ter espaço no meio.
 */
const PADRAO_SEM_ESPACO = /^\S+$/;

/** Espelha `OPCOES_DE_ENDERECO` do DTO: protocolo obrigatório, domínio não. */
const PADRAO_DE_PROTOCOLO = /^https?:\/\//i;

const MAXIMO_DE_ESCOPOS = 20;
const MAXIMO_DE_CARACTERES_DO_ESCOPO = 64;
const MAXIMO_DE_CARACTERES_DO_CLIENT_ID = 255;
const MAXIMO_DE_CARACTERES_DO_ALIAS = 64;

const MSG_ENDERECO_EMISSOR = 'Informe o endereço do emissor começando com http:// ou https://.';
const MSG_ENDERECO_RETORNO = 'Informe o endereço de retorno começando com http:// ou https://.';
const MSG_CLIENT_ID_LONGO = `O identificador do cliente deve ter no máximo ${MAXIMO_DE_CARACTERES_DO_CLIENT_ID} caracteres.`;
const MSG_ALIAS_ESPACO = 'O apelido do provedor não pode conter espaços.';
const MSG_ALIAS_LONGO = `O apelido do provedor deve ter no máximo ${MAXIMO_DE_CARACTERES_DO_ALIAS} caracteres.`;
const MSG_ESCOPO_VAZIO = 'Informe pelo menos um escopo.';
const MSG_ESCOPO_MAXIMO = `Informe no máximo ${MAXIMO_DE_ESCOPOS} escopos.`;
const MSG_ESCOPO_LONGO = `Cada escopo deve ter no máximo ${MAXIMO_DE_CARACTERES_DO_ESCOPO} caracteres.`;

/**
 * Como um endereço é aceito — texto vazio incluído, porque a validação de
 * "vazio vira nulo" é responsabilidade de outro passo (`paraDadosAmbiente`),
 * não deste `refine`. Domínio de primeiro nível NÃO é exigido: e por isso
 * `http://localhost:8081` e `http://govbr-simulador:8080` passam. É o mesmo
 * critério do servidor (`OPCOES_DE_ENDERECO`, `require_tld: false`) — recusar
 * aqui o que o servidor aceita produziria um formulário mais estrito que a
 * regra real, e a pessoa não teria como saber por quê.
 */
function enderecoValido(valor: string): boolean {
  if (valor === '') {
    return true;
  }

  if (!PADRAO_DE_PROTOCOLO.test(valor)) {
    return false;
  }

  try {
    const enderecoConstruido = new URL(valor);
    return Boolean(enderecoConstruido);
  } catch {
    return false;
  }
}

const esquema = z
  .object({
    modo: z.enum(MODOS),
    issuerUrl: z.string().trim().refine(enderecoValido, MSG_ENDERECO_EMISSOR),
    clientId: z.string().trim().max(MAXIMO_DE_CARACTERES_DO_CLIENT_ID, MSG_CLIENT_ID_LONGO),
    redirectUri: z.string().trim().refine(enderecoValido, MSG_ENDERECO_RETORNO),
    aliasIdp: z
      .string()
      .trim()
      .max(MAXIMO_DE_CARACTERES_DO_ALIAS, MSG_ALIAS_LONGO)
      .refine((valor) => valor === '' || PADRAO_SEM_ESPACO.test(valor), MSG_ALIAS_ESPACO),
    escoposTexto: z.string(),
    nivelMinimo: z.enum(NIVEIS_MINIMOS),
    ativo: z.boolean(),
  })
  .superRefine((valores, ctx) => {
    // Os escopos vivem num único campo de texto (item 6 do enunciado): a
    // conversão para lista é feita por `escoposDoTexto`, a MESMA função que a
    // tabela de tentativas e o resto do módulo usam — nenhuma regra de split
    // nasce duas vezes.
    const escopos = escoposDoTexto(valores.escoposTexto);

    if (escopos.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['escoposTexto'],
        message: MSG_ESCOPO_VAZIO,
      });
      return;
    }

    if (escopos.length > MAXIMO_DE_ESCOPOS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['escoposTexto'],
        message: MSG_ESCOPO_MAXIMO,
      });
      return;
    }

    if (escopos.some((escopo) => escopo.length > MAXIMO_DE_CARACTERES_DO_ESCOPO)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['escoposTexto'],
        message: MSG_ESCOPO_LONGO,
      });
    }
  });

type ValoresFormulario = z.infer<typeof esquema>;

function valoresIniciais(ambiente: AmbienteGovbr): ValoresFormulario {
  return {
    modo: ambiente.modo,
    issuerUrl: ambiente.issuer_url ?? '',
    clientId: ambiente.client_id ?? '',
    redirectUri: ambiente.redirect_uri ?? '',
    aliasIdp: ambiente.alias_idp ?? '',
    escoposTexto: textoDosEscopos(ambiente.escopos),
    nivelMinimo: ambiente.nivel_minimo,
    ativo: ambiente.ativo,
  };
}

/**
 * Texto vazio em campo opcional significa nulo, nunca string vazia — o
 * servidor recusa `""` (govbr.dto.ts, comentário de `issuer_url`). Os campos
 * chegam já cortados de espaço nas pontas (`.trim()` do esquema).
 */
function paraNuloOuTexto(valor: string): string | null {
  return valor === '' ? null : valor;
}

function paraDadosAmbiente(valores: ValoresFormulario): DadosAmbiente {
  return {
    modo: valores.modo,
    nivel_minimo: valores.nivelMinimo,
    ativo: valores.ativo,
    escopos: escoposDoTexto(valores.escoposTexto),
    issuer_url: paraNuloOuTexto(valores.issuerUrl),
    client_id: paraNuloOuTexto(valores.clientId),
    redirect_uri: paraNuloOuTexto(valores.redirectUri),
    alias_idp: paraNuloOuTexto(valores.aliasIdp),
  };
}

export function FormularioDoAmbiente({
  ambiente,
  aoGravar,
  gravando,
  aoTestar,
  testando,
  resultado,
}: FormularioDoAmbienteProps): ReactElement {
  const idBase = useId();

  const form = useForm<ValoresFormulario>({
    resolver: zodResolver(esquema),
    defaultValues: valoresIniciais(ambiente),
    mode: 'onSubmit',
  });

  /**
   * O PONTO SUTIL DESTE ARQUIVO: quando resincronizar os campos com `ambiente`.
   *
   * O teste de conexão grava `ultima_verificacao_em`/`ultima_verificacao_ok` na
   * MESMA linha (dados.ts, `useTestarConexao`), e a página busca de novo depois
   * — a prop `ambiente` muda de referência a cada teste. Se resetássemos o
   * formulário a cada mudança de `ambiente`, apagaríamos o que a pessoa estiver
   * digitando no meio de um teste. Por isso as duas colunas de verificação NÃO
   * entram no `useForm`: são lidas direto de `ambiente` no render (mais abaixo),
   * e por isso elas SEMPRE aparecem atualizadas, sem precisar de reset nenhum.
   *
   * O reset só acontece quando `ambiente.ambiente` (o código — DSV/HML/PRD)
   * muda: aí é outra linha de verdade, e manter o que estava no campo seria
   * mostrar o rascunho de um ambiente dentro do outro.
   */
  const identidadeAtual = useRef(ambiente.ambiente);

  useEffect(() => {
    if (identidadeAtual.current === ambiente.ambiente) {
      return;
    }

    identidadeAtual.current = ambiente.ambiente;
    form.reset(valoresIniciais(ambiente));
  }, [ambiente, form]);

  function enviar(valores: ValoresFormulario): void {
    aoGravar(paraDadosAmbiente(valores));
  }

  /**
   * O teste roda no servidor sobre o que já está GRAVADO — `aoTestar` não leva
   * argumento nenhum (a rota é `POST /govbr/ambientes/:ambiente/testar`, sem
   * corpo). Por isso a decisão de desabilitar o botão olha `ambiente.issuer_url`
   * — o valor salvo — e não o campo do formulário, que pode estar com uma
   * edição ainda não gravada. Antecipar com o valor salvo é dizer a verdade
   * sobre o que o clique vai realmente testar.
   */
  const semEmissorSalvo = !ambiente.issuer_url || ambiente.issuer_url.trim() === '';
  const testeDesabilitado = testando || semEmissorSalvo;
  const idAjudaTeste = `${idBase}-ajuda-teste`;
  const idLegendaModo = `${idBase}-legenda-modo`;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(enviar)} className="space-y-6" noValidate>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="modo"
            render={({ field }) => (
              <FormItem className="sm:col-span-2">
                <p
                  id={idLegendaModo}
                  className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                >
                  Modo
                </p>
                <FormControl>
                  <RadioGroup
                    value={field.value}
                    onValueChange={field.onChange}
                    aria-labelledby={idLegendaModo}
                    className="grid gap-3 sm:grid-cols-2"
                  >
                    {MODOS.map((modo) => (
                      <RadioCartaoDeModo
                        key={modo}
                        idBase={idBase}
                        modo={modo}
                        selecionado={field.value === modo}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="issuerUrl"
            render={({ field }) => (
              <FormItem className="sm:col-span-2">
                <FormLabel>Endereço do emissor</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="text"
                    inputMode="url"
                    autoComplete="off"
                    placeholder="https://sso.acesso.gov.br"
                  />
                </FormControl>
                <FormDescription>
                  Endereço base do provedor de identidade. Em branco enquanto o ambiente não estiver
                  configurado.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="clientId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Identificador do cliente</FormLabel>
                <FormControl>
                  <Input {...field} type="text" autoComplete="off" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="redirectUri"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Endereço de retorno</FormLabel>
                <FormControl>
                  <Input {...field} type="text" inputMode="url" autoComplete="off" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="aliasIdp"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Apelido do provedor</FormLabel>
                <FormControl>
                  <Input {...field} type="text" autoComplete="off" placeholder="govbr" />
                </FormControl>
                <FormDescription>
                  Usado pelo Keycloak como kc_idp_hint. Sem espaços.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="escoposTexto"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Escopos solicitados</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="text"
                    autoComplete="off"
                    placeholder="openid email profile"
                  />
                </FormControl>
                <FormDescription>
                  Separe os escopos por espaço. Pelo menos um é obrigatório.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="nivelMinimo"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nível mínimo de confiabilidade</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {NIVEIS_MINIMOS.map((nivel) => (
                      <SelectItem key={nivel} value={nivel}>
                        {ROTULO_DO_NIVEL[nivel]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="ativo"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between gap-4 rounded-xl border border-input p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-sm font-medium normal-case tracking-normal text-foreground">
                    Ambiente ativo
                  </FormLabel>
                  <FormDescription>
                    Desligado, o ambiente não entra no fluxo de login.
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <div className="space-y-4 border-t border-border pt-6">
          {/*
            Fora dos campos editáveis, de propósito: lido direto de `ambiente`,
            não do estado do formulário — ver o comentário do `useEffect` acima.
          */}
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-medium text-foreground">Última verificação:</span>
            {ambiente.ultima_verificacao_em === null ? (
              <span className="text-muted-foreground">nunca verificado</span>
            ) : (
              <>
                <StatusBadge
                  status={ambiente.ultima_verificacao_ok ? 'aprovado' : 'rejeitado'}
                  label={ambiente.ultima_verificacao_ok ? 'Sucesso' : 'Falha'}
                />
                <span className="text-muted-foreground">
                  {dataHoraLegivel(ambiente.ultima_verificacao_em)}
                </span>
              </>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={aoTestar}
              disabled={testeDesabilitado}
              aria-busy={testando}
              aria-describedby={semEmissorSalvo && !testando ? idAjudaTeste : undefined}
            >
              {testando ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  Testando…
                </>
              ) : (
                'Testar conexão'
              )}
            </Button>
            {semEmissorSalvo && !testando && (
              <p id={idAjudaTeste} className="text-sm text-muted-foreground">
                Grave um endereço de emissor antes de testar a conexão.
              </p>
            )}
          </div>

          {resultado && <ResultadoDoTeste resultado={resultado} />}
        </div>

        <div className="flex justify-end border-t border-border pt-6">
          <Button type="submit" disabled={gravando} aria-busy={gravando}>
            {gravando ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Salvando…
              </>
            ) : (
              'Salvar'
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}

interface RadioCartaoDeModoProps {
  idBase: string;
  modo: Modo;
  selecionado: boolean;
}

/**
 * Um cartão clicável por modo, com rótulo e descrição juntos — não só o nome
 * (`DESCRICAO_DO_MODO`), porque "simulado" e "real" não dizem por si o que
 * cada um faz.
 *
 * O `<label>` ENVOLVE o rádio e o texto: é a associação nativa, sem precisar
 * de `htmlFor`/`id` combinando à mão — clicar em qualquer parte do cartão
 * marca a opção.
 */
function RadioCartaoDeModo({ idBase, modo, selecionado }: RadioCartaoDeModoProps): ReactElement {
  const id = `${idBase}-modo-${modo}`;

  return (
    <label
      htmlFor={id}
      className={cn(
        'flex cursor-pointer items-start gap-3 rounded-xl border border-input p-4 transition-colors',
        selecionado && 'border-primary bg-primary/5',
      )}
    >
      <RadioGroupItem value={modo} id={id} className="mt-0.5" />
      <span className="space-y-1">
        <span className="block text-sm font-medium text-foreground">{ROTULO_DO_MODO[modo]}</span>
        <span className="block text-xs text-muted-foreground">{DESCRICAO_DO_MODO[modo]}</span>
      </span>
    </label>
  );
}

interface ResultadoDoTesteProps {
  resultado: ResultadoTeste;
}

/**
 * O resultado do teste disparado NESTA sessão — diferente da "última
 * verificação" acima, que é o que está gravado na linha.
 *
 * `sucesso: false` NÃO é erro de sistema: o emissor fora do ar responde 200
 * com o motivo (criterio E4.3, docs/CONECTOR-GOVBR.md, seção 5). Por isso o
 * ícone de falha é `Info`, não um alerta — e a cor não é a destrutiva. O que
 * aparece é sempre `resultado.mensagem`, a frase em português que a API já
 * grava pronta (`MOTIVOS` em govbr.tipos.ts); o código cru (`SEM_S256`,
 * `EMISSOR_INACESSIVEL`) nunca é lido por este componente.
 */
function ResultadoDoTeste({ resultado }: ResultadoDoTesteProps): ReactElement {
  return (
    <div role="status" aria-live="polite">
      <Alert className={resultado.sucesso ? 'border-success/40 bg-success/10' : undefined}>
        {resultado.sucesso ? (
          <CheckCircle2 className="text-success" aria-hidden="true" />
        ) : (
          <Info className="text-muted-foreground" aria-hidden="true" />
        )}
        <AlertTitle className={resultado.sucesso ? 'text-success' : undefined}>
          {resultado.sucesso ? 'Conexão testada com sucesso' : 'A conexão não foi confirmada'}
        </AlertTitle>
        <AlertDescription className={cn('space-y-1', resultado.sucesso && 'text-success')}>
          <p>{resultado.mensagem}</p>
          <p className="text-xs text-muted-foreground">
            {duracaoLegivel(resultado.duracao_ms)} · {dataHoraLegivel(resultado.verificado_em)}
          </p>
        </AlertDescription>
      </Alert>
    </div>
  );
}

export default FormularioDoAmbiente;
